<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    max-width="68rem"
    content-class="live-quiz-editor-dialog">
    <v-card dark class="quiz-editor-card">
      <header class="editor-header">
        <span class="editor-mark"><v-icon>mdi-head-question-outline</v-icon></span>
        <div>
          <small>{{ form._id ? "QUIZ BEARBEITEN" : "NEUES QUIZ" }}</small>
          <h2>{{ form._id ? form.title || "Unbenanntes Quiz" : "Neue Quiz-Runde vorbereiten" }}</h2>
          <p>Fragen, Antworten und Zeitlimit festlegen.</p>
        </div>
        <v-btn icon class="editor-close" aria-label="Editor schliessen" :disabled="saving" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </header>

      <v-card-text class="editor-body">
        <section class="editor-section basics-section">
          <div class="section-title">
            <span><v-icon>mdi-text-box-outline</v-icon></span>
            <div><h3>Grundlagen</h3><p>Name und kurze Einordnung des Quiz.</p></div>
          </div>
          <v-row>
            <v-col cols="12" md="7">
              <v-text-field v-model="form.title" label="Quiz-Titel" outlined dark hide-details="auto" maxlength="160" counter="160" />
            </v-col>
            <v-col cols="12" md="5">
              <div class="status-preview" :class="`status-${form.status}`">
                <span><i></i>{{ statusLabel(form.status) }}</span>
                <small>{{ form.questions.length }} {{ form.questions.length === 1 ? "Frage" : "Fragen" }}</small>
              </div>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.description"
                label="Beschreibung (optional)"
                outlined
                dark
                rows="2"
                auto-grow
                hide-details="auto"
                maxlength="1000"
                counter="1000" />
            </v-col>
          </v-row>
        </section>

        <div class="questions-heading">
          <div><span>QUIZ-INHALT</span><h3>Fragen &amp; Antworten</h3></div>
          <span class="question-count">{{ form.questions.length }} / 100</span>
        </div>

        <section v-for="(question, questionIndex) in form.questions" :key="question._key" class="question-card">
          <header class="question-card-header">
            <span class="question-number">{{ questionIndex + 1 }}</span>
            <div><small>{{ questionTypeLabel(question.type).toUpperCase() }} · FRAGE {{ questionIndex + 1 }}</small><strong>{{ question.text || "Noch ohne Fragetext" }}</strong></div>
            <div class="question-actions">
              <v-btn icon small :disabled="questionIndex === 0" aria-label="Frage nach oben" @click="moveQuestion(questionIndex, -1)"><v-icon>mdi-arrow-up</v-icon></v-btn>
              <v-btn icon small :disabled="questionIndex === form.questions.length - 1" aria-label="Frage nach unten" @click="moveQuestion(questionIndex, 1)"><v-icon>mdi-arrow-down</v-icon></v-btn>
              <v-btn icon small color="#ff7c89" aria-label="Frage entfernen" @click="removeQuestion(questionIndex)"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </div>
          </header>

          <div class="question-fields">
            <v-textarea
              v-model="question.text"
              :label="`Fragetext ${questionIndex + 1}`"
              outlined
              dark
              auto-grow
              rows="2"
              maxlength="500"
              hide-details="auto" />
            <div class="question-config-stack">
              <v-select
                v-model="question.type"
                :items="questionTypeOptions"
                item-text="label"
                item-value="value"
                label="Fragetyp"
                outlined
                dark
                hide-details="auto"
                prepend-inner-icon="mdi-shape-outline"
                @change="handleQuestionTypeChange(question)" />
              <v-select
                v-model="question.timeLimitSeconds"
                :items="timeOptions"
                item-text="label"
                item-value="value"
                label="Antwortzeit"
                outlined
                dark
                hide-details="auto"
                prepend-inner-icon="mdi-timer-outline" />
            </div>
          </div>

          <div v-if="question.type === 'estimate'" class="estimate-editor">
            <div class="estimate-heading">
              <span><v-icon>mdi-chart-bell-curve-cumulative</v-icon></span>
              <div><strong>Schätzfrage konfigurieren</strong><small>Der Zielwert bleibt für Teilnehmende bis zur Auflösung verborgen.</small></div>
            </div>
            <div class="estimate-fields">
              <v-text-field
                v-model="question.targetValue"
                label="Richtiger Zielwert"
                inputmode="decimal"
                outlined
                dark
                hide-details="auto"
                prepend-inner-icon="mdi-target" />
              <v-text-field
                v-model="question.unit"
                label="Einheit (optional)"
                placeholder="z. B. kg, CHF oder Jahre"
                maxlength="40"
                outlined
                dark
                hide-details="auto" />
              <v-select
                v-model="question.estimateMode"
                :items="estimateModeOptions"
                item-text="label"
                item-value="value"
                label="Bewertung"
                outlined
                dark
                hide-details="auto" />
              <v-text-field
                v-if="question.estimateMode === 'tolerance'"
                v-model="question.tolerancePercent"
                label="Erlaubte Abweichung"
                suffix="%"
                inputmode="decimal"
                outlined
                dark
                hide-details="auto" />
            </div>
            <p class="estimate-note">
              <v-icon>mdi-information-outline</v-icon>
              {{ question.estimateMode === "closest" ? "Der nächste Wert gewinnt; Gleichstände erhalten dieselbe Punktzahl." : "Innerhalb der Toleranz gibt es je nach Nähe zwischen 500 und 1000 Punkte." }}
            </p>
          </div>

          <template v-else>
            <div class="answer-editor-heading">
              <div>
                <strong>Antworten</strong>
                <small>{{ question.type === "multiple" ? "Mehrere richtige Antworten markieren; falsche Auswahl reduziert Teilpunkte." : "Genau eine Antwort als richtig markieren." }}</small>
              </div>
              <v-btn text color="#58d388" :disabled="question.answers.length >= 4" @click="addAnswer(question)">
                <v-icon left>mdi-plus</v-icon>Antwort
              </v-btn>
            </div>

            <div class="answer-editor-list">
              <div
                v-for="(answer, answerIndex) in question.answers"
                :key="answer._key"
                class="answer-editor-row"
                :class="[answerTone(answerIndex), { correct: answer.correct }]">
                <button
                  type="button"
                  class="correct-toggle"
                  :aria-label="answer.correct ? 'Als richtige Antwort markiert' : 'Als richtige Antwort markieren'"
                  :aria-pressed="answer.correct ? 'true' : 'false'"
                  @click="toggleCorrectAnswer(question, answerIndex)">
                  <v-icon>{{ answer.correct ? "mdi-check-circle" : "mdi-circle-outline" }}</v-icon>
                </button>
                <span class="answer-shape"><v-icon>{{ answerIcon(answerIndex) }}</v-icon></span>
                <v-text-field
                  v-model="answer.text"
                  :label="`Antwort ${answerIndex + 1}`"
                  outlined
                  dense
                  dark
                  hide-details="auto"
                  maxlength="240" />
                <v-btn
                  icon
                  small
                  :disabled="question.answers.length <= minimumAnswers(question)"
                  aria-label="Antwort entfernen"
                  @click="removeAnswer(question, answerIndex)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </section>

        <button v-if="form.questions.length < 100" type="button" class="add-question-button" @click="addQuestion">
          <span><v-icon>mdi-plus</v-icon></span><div><strong>Neue Frage hinzufügen</strong><small>Mit zwei Antwortmöglichkeiten und 20 Sekunden starten.</small></div><v-icon>mdi-chevron-right</v-icon>
        </button>

        <div v-if="validationError" class="validation-error" role="alert">
          <v-icon>mdi-alert-circle-outline</v-icon><span>{{ validationError }}</span>
        </div>
      </v-card-text>

      <v-card-actions class="editor-footer">
        <div class="footer-status"><span :class="`status-${form.status}`"><i></i>{{ statusLabel(form.status) }}</span><small>Änderungen werden erst nach dem Speichern aktiv.</small></div>
        <v-spacer></v-spacer>
        <v-btn text color="#aeb8b2" large :disabled="saving" @click="close">Abbrechen</v-btn>
        <v-btn outlined color="#aeb8b2" large :loading="saving && savingStatus === 'draft'" :disabled="saving" @click="save('draft')">
          {{ form.status === "archived" ? "Wiederherstellen" : "Entwurf speichern" }}
        </v-btn>
        <v-btn color="#58d388" class="publish-button" large depressed :loading="saving && savingStatus === 'published'" :disabled="saving" @click="save('published')">
          <v-icon left>mdi-broadcast</v-icon>{{ form.status === "published" ? "Speichern" : "Veröffentlichen" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
function createKey(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function newAnswer(correct = false) {
  return { _key: createKey("answer"), text: "", correct }
}

function newQuestion() {
  return {
    _key: createKey("question"),
    text: "",
    type: "single",
    timeLimitSeconds: 20,
    targetValue: null,
    unit: "",
    estimateMode: "closest",
    tolerancePercent: 10,
    answers: [newAnswer(true), newAnswer(false)],
  }
}

function parseNumericValue(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  let normalized = String(value == null ? "" : value)
    .trim()
    .replace(/[\s']/g, "")
  if (!normalized) return null
  if (normalized.includes(",") && normalized.includes(".")) {
    if (normalized.lastIndexOf(",") > normalized.lastIndexOf(".")) normalized = normalized.replace(/\./g, "").replace(",", ".")
    else normalized = normalized.replace(/,/g, "")
  } else normalized = normalized.replace(",", ".")
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export default {
  name: "LiveQuizEditorDialog",
  props: {
    value: { type: Boolean, default: false },
    quiz: { type: Object, default: null },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      form: this.createEmptyForm(),
      validationError: "",
      savingStatus: null,
      questionTypeOptions: [
        { label: "Einzelwahl", value: "single" },
        { label: "Mehrfachauswahl", value: "multiple" },
        { label: "Schätzfrage", value: "estimate" },
      ],
      estimateModeOptions: [
        { label: "Nächster Wert gewinnt", value: "closest" },
        { label: "Prozent-Toleranz", value: "tolerance" },
      ],
      timeOptions: [
        { label: "5 Sekunden", value: 5 },
        { label: "10 Sekunden", value: 10 },
        { label: "15 Sekunden", value: 15 },
        { label: "20 Sekunden", value: 20 },
        { label: "30 Sekunden", value: 30 },
        { label: "45 Sekunden", value: 45 },
        { label: "60 Sekunden", value: 60 },
        { label: "90 Sekunden", value: 90 },
        { label: "120 Sekunden", value: 120 },
      ],
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
  },
  watch: {
    value(open) {
      if (open) this.resetForm()
    },
    saving(saving) {
      if (!saving) this.savingStatus = null
    },
  },
  methods: {
    createEmptyForm() {
      return { title: "", description: "", status: "draft", questions: [newQuestion()] }
    },
    resetForm() {
      this.validationError = ""
      if (!this.quiz) {
        this.form = this.createEmptyForm()
        return
      }
      this.form = {
        _id: this.quiz._id,
        title: this.quiz.title || "",
        description: this.quiz.description || "",
        status: this.quiz.status || "draft",
        questions: (this.quiz.questions || []).map((question) => ({
          _id: question._id,
          _key: createKey("question"),
          text: question.text || "",
          type: ["single", "multiple", "estimate"].includes(question.type) ? question.type : "single",
          timeLimitSeconds: Number(question.timeLimitSeconds) || 20,
          targetValue: question.targetValue == null ? null : question.targetValue,
          unit: question.unit || "",
          estimateMode: question.estimateMode === "tolerance" ? "tolerance" : "closest",
          tolerancePercent: question.tolerancePercent == null ? 10 : question.tolerancePercent,
          answers: (question.answers || []).map((answer) => ({
            _id: answer._id,
            _key: createKey("answer"),
            text: answer.text || "",
            correct: Boolean(answer.correct),
          })),
        })),
      }
      if (!this.form.questions.length) this.form.questions = [newQuestion()]
      this.form.questions.forEach((question) => {
        while (question.answers.length < 2) question.answers.push(newAnswer(question.answers.length === 0))
        this.handleQuestionTypeChange(question)
      })
    },
    addQuestion() {
      this.form.questions.push(newQuestion())
    },
    removeQuestion(index) {
      this.form.questions.splice(index, 1)
      if (!this.form.questions.length) this.form.questions.push(newQuestion())
    },
    moveQuestion(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.form.questions.length) return
      const [question] = this.form.questions.splice(index, 1)
      this.form.questions.splice(targetIndex, 0, question)
    },
    addAnswer(question) {
      if (question.answers.length < 4) question.answers.push(newAnswer(false))
    },
    removeAnswer(question, index) {
      if (question.answers.length <= this.minimumAnswers(question)) return
      question.answers.splice(index, 1)
      this.handleQuestionTypeChange(question)
    },
    toggleCorrectAnswer(question, index) {
      if (question.type === "multiple") {
        question.answers[index].correct = !question.answers[index].correct
        return
      }
      question.answers.forEach((answer, answerIndex) => {
        answer.correct = answerIndex === index
      })
    },
    minimumAnswers(question) {
      return question.type === "multiple" ? 3 : 2
    },
    handleQuestionTypeChange(question) {
      if (question.type === "estimate") return
      const minimum = this.minimumAnswers(question)
      while (question.answers.length < minimum) question.answers.push(newAnswer(false))
      if (question.type === "multiple") {
        if (question.answers.filter((answer) => answer.correct).length < 2) {
          question.answers.forEach((answer, index) => {
            answer.correct = index < 2
          })
        }
        if (question.answers.every((answer) => answer.correct)) question.answers[question.answers.length - 1].correct = false
      } else {
        const firstCorrectIndex = Math.max(0, question.answers.findIndex((answer) => answer.correct))
        question.answers.forEach((answer, index) => {
          answer.correct = index === firstCorrectIndex
        })
      }
    },
    validate(status) {
      if (!String(this.form.title || "").trim()) return "Bitte einen Quiz-Titel erfassen."
      if (status !== "published") return ""
      if (!this.form.questions.length) return "Ein veröffentlichtes Quiz benötigt mindestens eine Frage."
      for (let questionIndex = 0; questionIndex < this.form.questions.length; questionIndex += 1) {
        const question = this.form.questions[questionIndex]
        if (!String(question.text || "").trim()) return `Bitte den Text für Frage ${questionIndex + 1} erfassen.`
        if (question.type === "estimate") {
          const targetValue = parseNumericValue(question.targetValue)
          const tolerancePercent = parseNumericValue(question.tolerancePercent)
          if (targetValue == null) return `Bitte den Zielwert für Schätzfrage ${questionIndex + 1} erfassen.`
          if (Math.abs(targetValue) > 1000000000000000) return `Der Zielwert von Schätzfrage ${questionIndex + 1} ist zu gross.`
          if (question.estimateMode === "tolerance" && targetValue === 0) {
            return `Der Zielwert von Schätzfrage ${questionIndex + 1} darf bei Prozent-Toleranz nicht 0 sein.`
          }
          if (question.estimateMode === "tolerance" && !(tolerancePercent > 0)) {
            return `Bitte eine positive Prozent-Toleranz für Frage ${questionIndex + 1} erfassen.`
          }
          continue
        }
        if (question.answers.length < 2) return `Frage ${questionIndex + 1} benötigt mindestens zwei Antworten.`
        if (question.answers.some((answer) => !String(answer.text || "").trim())) return `Bitte alle Antworten von Frage ${questionIndex + 1} ausfüllen.`
        const correctCount = question.answers.filter((answer) => answer.correct).length
        if (question.type === "single" && correctCount !== 1) return `Frage ${questionIndex + 1} benötigt genau eine richtige Antwort.`
        if (question.type === "multiple" && correctCount < 2) return `Frage ${questionIndex + 1} benötigt mindestens zwei richtige Antworten.`
        if (question.type === "multiple" && correctCount >= question.answers.length) {
          return `Frage ${questionIndex + 1} benötigt mindestens eine falsche Antwortmöglichkeit.`
        }
      }
      return ""
    },
    save(status) {
      this.validationError = this.validate(status)
      if (this.validationError) return
      this.savingStatus = status
      const payload = {
        _id: this.form._id,
        title: String(this.form.title || "").trim(),
        description: String(this.form.description || "").trim(),
        status,
        questions: this.form.questions.map((question) => ({
          _id: question._id,
          text: String(question.text || "").trim(),
          type: question.type || "single",
          timeLimitSeconds: Number(question.timeLimitSeconds) || 20,
          targetValue: question.type === "estimate" ? parseNumericValue(question.targetValue) : null,
          unit: question.type === "estimate" ? String(question.unit || "").trim() : "",
          estimateMode: question.estimateMode === "tolerance" ? "tolerance" : "closest",
          tolerancePercent: parseNumericValue(question.tolerancePercent) || 10,
          answers: question.answers.map((answer) => ({
            _id: answer._id,
            text: String(answer.text || "").trim(),
            correct: Boolean(answer.correct),
          })),
        })),
      }
      this.$emit("save", payload)
    },
    close() {
      if (this.saving) return
      this.dialog = false
    },
    statusLabel(status) {
      return { draft: "Entwurf", published: "Veröffentlicht", archived: "Archiviert" }[status] || "Entwurf"
    },
    questionTypeLabel(type) {
      return { single: "Einzelwahl", multiple: "Mehrfachauswahl", estimate: "Schätzfrage" }[type] || "Einzelwahl"
    },
    answerTone(index) {
      return ["tone-coral", "tone-blue", "tone-amber", "tone-violet"][index % 4]
    },
    answerIcon(index) {
      return ["mdi-triangle", "mdi-rhombus", "mdi-circle", "mdi-square"][index % 4]
    },
  },
}
</script>

<style scoped>
.quiz-editor-card { height: min(92vh, 62rem); overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 1.5rem !important; background: #101512 !important; font-size: 1rem; }
.editor-header { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.875rem; padding: 1.125rem 1.25rem; border-bottom: 1px solid rgba(255, 255, 255, 0.08); background: linear-gradient(120deg, rgba(88, 211, 136, 0.075), transparent 48%), #111713; }
.editor-mark { display: grid; width: 3rem; height: 3rem; place-items: center; border-radius: 1rem; background: rgba(88, 211, 136, 0.12); }
.editor-mark .v-icon { color: #58d388; }
.editor-header > div { min-width: 0; }
.editor-header small { color: #58d388; font-size: 0.75rem; font-weight: 850; letter-spacing: 0.12em; }
.editor-header h2 { margin: 0.15rem 0 0; overflow: hidden; font-size: 1.375rem; text-overflow: ellipsis; white-space: nowrap; }
.editor-header p { margin: 0.2rem 0 0; color: #8f9c95; font-size: 0.875rem; }
.editor-close { border: 1px solid rgba(255, 255, 255, 0.1); background: rgba(255, 255, 255, 0.04); }
.editor-body { padding: 1.25rem !important; background: #0c100e; }
.editor-section { padding: 1.125rem; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 1.25rem; background: rgba(255, 255, 255, 0.035); }
.section-title { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
.section-title > span { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; background: rgba(88, 211, 136, 0.1); }
.section-title > span .v-icon { color: #58d388; }
.section-title h3 { margin: 0; font-size: 1.125rem; }
.section-title p { margin: 0.2rem 0 0; color: #8f9c95; font-size: 0.875rem; }
.status-preview { display: flex; min-height: 3.5rem; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0 0.875rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.18); }
.status-preview > span,
.footer-status > span { display: inline-flex; align-items: center; gap: 0.4rem; color: #aab5af; font-size: 0.75rem; font-weight: 800; }
.status-preview i,
.footer-status i { width: 0.45rem; height: 0.45rem; border-radius: 50%; background: currentColor; }
.status-preview .status-published,
.footer-status .status-published,
.status-preview.status-published > span { color: #58d388; }
.status-preview.status-archived > span { color: #7f8b84; }
.status-preview > small { color: #87948d; font-size: 0.75rem; }
.questions-heading { display: flex; align-items: flex-end; justify-content: space-between; margin: 1.5rem 0 0.75rem; }
.questions-heading > div > span { color: #58d388; font-size: 0.75rem; font-weight: 850; letter-spacing: 0.12em; }
.questions-heading h3 { margin: 0.2rem 0 0; font-size: 1.25rem; }
.question-count { padding: 0.35rem 0.6rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 0.625rem; color: #929f98; font-size: 0.75rem; font-weight: 800; }
.question-card { margin-bottom: 0.875rem; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 1.25rem; background: rgba(255, 255, 255, 0.03); }
.question-card-header { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; padding: 0.875rem 1rem; border-bottom: 1px solid rgba(255, 255, 255, 0.07); background: rgba(255, 255, 255, 0.025); }
.question-number { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.875rem; color: #07100a; background: #58d388; font-size: 0.875rem; font-weight: 950; }
.question-card-header > div:nth-child(2) { display: flex; min-width: 0; flex-direction: column; }
.question-card-header small { color: #58d388; font-size: 0.75rem; font-weight: 800; letter-spacing: 0.08em; }
.question-card-header strong { overflow: hidden; color: #dce3df; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.question-actions { display: flex; align-items: center; }
.question-actions .v-icon { font-size: 1.125rem; }
.question-fields { display: grid; grid-template-columns: minmax(0, 1fr) 13rem; gap: 0.75rem; padding: 1rem; }
.question-config-stack { display: grid; align-content: start; gap: 0.75rem; }
.estimate-editor { margin: 0 1rem 1rem; padding: 1rem; border: 1px solid rgba(73, 163, 255, 0.2); border-radius: 1rem; background: rgba(73, 163, 255, 0.055); }
.estimate-heading { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.875rem; }
.estimate-heading > span { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; background: rgba(73, 163, 255, 0.12); }
.estimate-heading > span .v-icon { color: #71b6ff; }
.estimate-heading > div { display: flex; flex-direction: column; }
.estimate-heading strong { font-size: 0.875rem; }
.estimate-heading small { color: #8d9b94; font-size: 0.75rem; }
.estimate-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.estimate-note { display: flex; align-items: center; gap: 0.5rem; margin: 0.875rem 0 0; color: #9daba4; font-size: 0.875rem; line-height: 1.5; }
.estimate-note .v-icon { color: #71b6ff; }
.answer-editor-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0 1rem 0.75rem; }
.answer-editor-heading > div { display: flex; flex-direction: column; }
.answer-editor-heading strong { font-size: 0.875rem; }
.answer-editor-heading small { color: #87958d; font-size: 0.75rem; }
.answer-editor-heading .v-btn { font-size: 0.875rem; text-transform: none; }
.answer-editor-list { display: grid; gap: 0.5rem; padding: 0 1rem 1rem; }
.answer-editor-row { display: grid; grid-template-columns: auto auto minmax(0, 1fr) auto; align-items: center; gap: 0.5rem; padding: 0.5rem; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.15); transition: border-color 160ms ease, background 160ms ease; }
.answer-editor-row.correct { border-color: rgba(88, 211, 136, 0.42); border-color: color-mix(in srgb, var(--answer-color), transparent 52%); background: rgba(88, 211, 136, 0.065); background: color-mix(in srgb, var(--answer-color), #0b0f0d 91%); }
.tone-coral { --answer-color: #ff5f68; }
.tone-blue { --answer-color: #49a3ff; }
.tone-amber { --answer-color: #f7c84b; }
.tone-violet { --answer-color: #b779ff; }
.correct-toggle { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; color: #718078; }
.correct-toggle .v-icon { color: currentColor; }
.answer-editor-row.correct .correct-toggle { color: #58d388; }
.answer-shape { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; background: rgba(255, 255, 255, 0.08); background: color-mix(in srgb, var(--answer-color), transparent 82%); }
.answer-shape .v-icon { color: var(--answer-color); font-size: 1.125rem; }
.add-question-button { display: grid; width: 100%; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; min-height: 5rem; padding: 0.75rem 1rem; border: 1px dashed rgba(88, 211, 136, 0.3); border-radius: 1.125rem; color: #dce4df; background: rgba(88, 211, 136, 0.045); text-align: left; }
.add-question-button > span { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; background: rgba(88, 211, 136, 0.12); }
.add-question-button .v-icon { color: #58d388; }
.add-question-button > div { display: flex; flex-direction: column; }
.add-question-button strong { font-size: 0.875rem; }
.add-question-button small { color: #89968f; font-size: 0.75rem; }
.validation-error { display: flex; align-items: center; gap: 0.625rem; margin-top: 1rem; padding: 0.75rem 0.875rem; border: 1px solid rgba(255, 111, 125, 0.28); border-radius: 0.875rem; color: #ff929d; background: rgba(255, 95, 104, 0.08); font-size: 0.875rem; }
.validation-error .v-icon { color: currentColor; }
.editor-footer { flex-wrap: wrap; gap: 0.5rem; padding: 0.875rem 1.25rem !important; border-top: 1px solid rgba(255, 255, 255, 0.08); background: #111713; }
.footer-status { display: flex; flex-direction: column; }
.footer-status > small { color: #748179; font-size: 0.75rem; }
.publish-button { color: #07100a !important; font-weight: 900 !important; text-transform: none !important; }
.editor-footer .v-btn { border-radius: 0.875rem; font-size: 0.875rem; text-transform: none; }

@media (max-width: 47.9375rem) {
  .quiz-editor-card { height: 100vh; border: 0; border-radius: 0 !important; }
  .editor-header { padding: 0.875rem; }
  .editor-mark { width: 2.75rem; height: 2.75rem; }
  .editor-header h2 { font-size: 1.125rem; }
  .editor-header p { display: none; }
  .editor-body { padding: 0.875rem !important; }
  .editor-section { padding: 0.875rem; }
  .question-fields { grid-template-columns: 1fr; padding: 0.875rem; }
  .estimate-editor { margin: 0 0.875rem 0.875rem; padding: 0.875rem; }
  .estimate-fields { grid-template-columns: 1fr; }
  .answer-editor-row { grid-template-columns: auto auto minmax(0, 1fr) auto; }
  .editor-footer { display: grid !important; grid-template-columns: 1fr 1fr; padding: 0.75rem !important; }
  .footer-status,
  .editor-footer .v-spacer,
  .editor-footer > .v-btn:first-of-type { display: none; }
  .editor-footer .v-btn { width: 100%; margin: 0 !important; }
}
</style>
